import { ColumnPin } from "@common/types/views/views";
import { RowData, RowSelectionState } from "@tanstack/react-table";
import type { ViewTableRow } from "./core/table";
import type React from "react";
import type { CSSProperties, MouseEvent } from "react";

export const BLOCK_EVENT_KEY = "view-click-block-event";

// Cast-free read of the group-boundary flag off a column's untyped meta bag.
export function hasGroupBoundary(meta: unknown): boolean {
    return (
        typeof meta === "object" &&
        meta !== null &&
        "isGroupBoundary" in meta &&
        meta.isGroupBoundary === true
    );
}

// A leaf whose class tier (meta.headerTierDepth) is shallower than the bottom
// header row lifts to that tier, so its label reads at its true level instead
// of sinking to the last row. Undefined when there's nothing to lift.
export function getLiftedHeaderTier(
    meta: unknown,
    headerRowCount: number,
): number | undefined {
    if (
        typeof meta !== "object" ||
        meta === null ||
        !("headerTierDepth" in meta) ||
        typeof meta.headerTierDepth !== "number"
    ) {
        return undefined;
    }
    const tierDepth = meta.headerTierDepth;
    if (tierDepth >= headerRowCount - 1) {
        return undefined;
    }
    return tierDepth;
}

// Where a row drop lands relative to the row the pointer is over:
// "before"/"after" → sibling insert above/below it; "on" → nest into it.
// Consumers that only reorder can ignore this and treat every drop the same.
export type RowDropPosition = "before" | "on" | "after";

// Resolves a drop position from the pointer's vertical position relative to the
// over-row's rect (kept in sync with the pointer-within collision detector, so
// the zone matches the row the pointer actually picks). Rows that can be nested
// into use quarter/half/quarter zones (top 25% before · middle 50% on · bottom
// 25% after); rows that cannot nest use halves (before/after only). When the
// pointer is unavailable it falls back to the safest neutral position: "on" for
// nestable rows, "before" for non-nestable ones (which never resolve to "on").
export function resolveRowDropPosition({
    pointerY,
    overTop,
    overHeight,
    canNest,
}: {
    pointerY: number | null;
    overTop: number;
    overHeight: number;
    canNest: boolean;
}): RowDropPosition {
    if (pointerY == null || overHeight <= 0) {
        return canNest ? "on" : "before";
    }
    const ratio = Math.min(Math.max((pointerY - overTop) / overHeight, 0), 1);
    if (!canNest) {
        return ratio < 0.5 ? "before" : "after";
    }
    if (ratio < 0.25) {
        return "before";
    }
    if (ratio > 0.75) {
        return "after";
    }
    return "on";
}

export interface ColumnPinData {
    isPinned: false | ColumnPin;
    isLastLeft: boolean;
    isFirstRight: boolean;
    style: CSSProperties;
}

const isSameStyle = (a: CSSProperties, b: CSSProperties) => {
    const aEntries = Object.entries(a);
    const bEntries = Object.entries(b);
    return (
        aEntries.length === bEntries.length &&
        aEntries.every(
            ([key, value], index) =>
                bEntries[index]?.[0] === key && bEntries[index]?.[1] === value,
        )
    );
};

/**
 * Whether two column pin entries draw the same. The pinning map is rebuilt on
 * every column-size change (e.g. each frame the table's container resizes),
 * so rows compare entries, not the map, to skip re-rendering when no pinned
 * offset moved.
 */
export const isSamePinData = (
    a: ColumnPinData | undefined,
    b: ColumnPinData | undefined,
) =>
    a === b ||
    (a !== undefined &&
        b !== undefined &&
        a.isPinned === b.isPinned &&
        a.isLastLeft === b.isLastLeft &&
        a.isFirstRight === b.isFirstRight &&
        isSameStyle(a.style, b.style));

/**
 * A pinned group header sticks with its columns: a left group holds the left
 * offset of its first column, a right group the right offset of its last one.
 */
export function getGroupHeaderPinData({
    leafColumnIds,
    columnPinningMap,
}: {
    leafColumnIds: string[];
    columnPinningMap: Map<string, ColumnPinData>;
}): ColumnPinData | undefined {
    const leafPinData = leafColumnIds.flatMap((id) => {
        const pinData = columnPinningMap.get(id);
        return pinData ? [pinData] : [];
    });
    const first = leafPinData.at(0);
    const last = leafPinData.at(-1);
    if (first?.isPinned === "left") {
        return { ...first, isLastLeft: last?.isLastLeft ?? false };
    }
    if (last?.isPinned === "right") {
        return { ...last, isFirstRight: first?.isFirstRight ?? false };
    }
    return undefined;
}

/**
 * Marks a mouse event as blocked so that the ViewTable row click handler
 * skips processing it. Use this instead of `(e as any)[BLOCK_EVENT_KEY] = true`.
 */
export function markEventBlocked(e: MouseEvent<HTMLElement>): void {
    (e as unknown as Record<string, boolean>)[BLOCK_EVENT_KEY] = true;
}

/**
 * Builds the row selection state for a shift-click range selection.
 * Resolves the anchor row ID to an index, computes the range, then
 * toggles: if every selectable row in the range is already selected
 * (or is the anchor row), deselects the range; otherwise selects it.
 */
export function buildShiftClickSelection<TData extends RowData>({
    visibleRows,
    anchorRowId,
    clickedIndex,
    prev,
}: {
    visibleRows: Array<ViewTableRow<TData>>;
    anchorRowId: string | null;
    clickedIndex: number;
    prev: RowSelectionState;
}): RowSelectionState {
    const anchorIndex =
        anchorRowId != null
            ? visibleRows.findIndex((r) => r.id === anchorRowId)
            : -1;
    const resolvedAnchor = anchorIndex !== -1 ? anchorIndex : clickedIndex;
    const from = Math.min(resolvedAnchor, clickedIndex);
    const to = Math.max(resolvedAnchor, clickedIndex);

    const selectableRows = visibleRows
        .slice(from, to + 1)
        .filter((r) => r.getCanSelect());
    const allSelected = selectableRows.every(
        (r) => prev[r.id] || r.id === anchorRowId,
    );

    const next = { ...prev };
    selectableRows.forEach((r) => {
        if (allSelected) {
            delete next[r.id];
        } else {
            next[r.id] = true;
        }
    });
    return next;
}

/**
 * Walks the virtualized row list from `fromIndex` in `direction`, skipping
 * display-only rows (full-width separators, load-more, errors). Stays put
 * when the walk runs off either end. ArrowUp/Down from no focus (`fromIndex`
 * < 0) land on the first focusable row, matching the previous ViewTable
 * behavior. Jump-to-bottom passes `fromIndex === rowCount`.
 */
export function findNextFocusableRowIndex({
    fromIndex,
    direction,
    rowCount,
    isSkippable,
}: {
    fromIndex: number;
    direction: 1 | -1;
    rowCount: number;
    isSkippable: (index: number) => boolean;
}): number {
    if (rowCount === 0) {
        return -1;
    }

    if (fromIndex < 0) {
        for (let index = 0; index < rowCount; index += 1) {
            if (!isSkippable(index)) {
                return index;
            }
        }
        return -1;
    }

    let index = fromIndex + direction;
    while (index >= 0 && index < rowCount) {
        if (!isSkippable(index)) {
            return index;
        }
        index += direction;
    }

    if (fromIndex < rowCount && !isSkippable(fromIndex)) {
        return fromIndex;
    }

    return -1;
}

export type ViewTableParentSelection = "self" | "cascade";

export interface ViewTableMeta<TCallbacks, TOptions> {
    callbacks?: TCallbacks;
    options?: TOptions;
    lastSelectedRowIdRef?: React.MutableRefObject<string | null>;
    getRowSelectionDisabledTooltip?: (row: unknown) => string | undefined;
    getRowSelectionHidden?: (row: unknown) => boolean;
    parentSelection?: ViewTableParentSelection;
    [key: string]: unknown;
}
